// ops-org-waap-domains-tab.tsx — WAAP website list under org detail (implicit customer token).
// Calls GET /api/v3/services/:id/waap/domains via impersonation-client for each WAAP service.
// Uses the org's CDN services list to find WAAP service ids, then links to the 13-tab WAAP console.
import { ShieldCheck, ExternalLink } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useActAsWAAPDomains, type ActAsWAAPDomain } from '@/api/act-as-customer-surfaces';
import { formatDate } from '@/lib/format';
import { TableLink } from '@/components/table-link';

/* ── Status tone helper ──────────────────────────────────────────────────── */

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'muted' | 'neutral' {
  switch (status) {
    case 'active': return 'success';
    case 'pending': return 'warning';
    case 'terminated': return 'muted';
    case 'suspended':
    case 'error': return 'danger';
    default: return 'neutral';
  }
}

/* ── Column factory ──────────────────────────────────────────────────────── */

function makeColumns(serviceId: string): ColumnDef<ActAsWAAPDomain, unknown>[] {
  return [
    {
      id: 'domain',
      accessorKey: 'domain',
      header: 'Domain',
      cell: ({ row }) => (
        <TableLink to={`/operations/act-as/waap/${serviceId}`}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
            {row.original.domain}
            <ExternalLink size={12} style={{ color: 'var(--vn-fg-subtle)', flexShrink: 0 }} />
          </span>
        </TableLink>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={statusTone(row.original.status ?? '')}>
          {row.original.status}
        </StatusPill>
      ),
    },
    {
      id: 'origin',
      header: 'Origin',
      cell: ({ row }) =>
        row.original.origin ? (
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 'var(--vn-font-size-xs)',
              color: 'var(--vn-fg-muted)',
            }}
          >
            {row.original.origin}
          </span>
        ) : (
          <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
        ),
    },
    {
      id: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
  ];
}

/* ── Tab component ───────────────────────────────────────────────────────── */

interface OpsOrgWaapDomainsTabProps {
  /** WAAP service id — needed to call /services/:id/waap/domains and link to console. */
  serviceId: string;
}

/**
 * WAAP website list for a specific WAAP service under the org.
 * Fetched via the org's cached customer token (set by OperationsOrgDetailPage on mount).
 * Each domain row links to /operations/act-as/waap/:serviceId for the 13-tab WAAP console.
 */
export function OpsOrgWaapDomainsTab({ serviceId }: OpsOrgWaapDomainsTabProps) {
  const query = useActAsWAAPDomains(serviceId, Boolean(serviceId));

  if (query.isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load WAAP websites"
        actions={
          <Button size="sm" variant="ghost" onClick={() => query.refetch()}>
            Retry
          </Button>
        }
      >
        The customer token may be unavailable or this service has no WAAP websites.
      </Alert>
    );
  }

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>WAAP Websites</CardTitle></CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  const rows = query.data ?? [];

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      >
        <CardTitle>WAAP Websites {rows.length > 0 ? `(${rows.length})` : ''}</CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={makeColumns(serviceId)}
          data={rows}
          getRowId={(r) => r.id}
          dense
          emptyState={
            <EmptyState
              icon={<ShieldCheck size={24} />}
              title="No WAAP websites"
              description="No WAAP websites found for this service."
            />
          }
          footer={
            rows.length > 0
              ? `${rows.length} website${rows.length !== 1 ? 's' : ''}`
              : undefined
          }
        />
      </CardBody>
    </Card>
  );
}
